import { TypedDocumentNode } from "@apollo/client";
import { Toast } from "@aurem/ui";
import { getTenantHeaderValue } from "@aurem/utils";
import { print } from "graphql";
import { gql } from "../../types";
import type { AuthTokens } from "../AuthProvider";

const API_URL = process.env.REACT_APP_GRAPHQL_API ?? "";

type JWTPayload = Record<string, unknown> & {
  exp?: number;
};

/**
 * Parses the payload out of a JWT token, WITHOUT VERIFYING THE SIGNATURE
 */
export function parseJWT(token: string): JWTPayload | null {
  try {
    // JWT tokens are composed of Base64 encoded HEADER, PAYLOAD, and VERIFY_SIGNATURE separated by '.'
    const [, base64Payload] = token.split(".");
    const payload = window.atob(base64Payload).toString();
    return JSON.parse(payload);
  } catch (err) {
    Toast.error({
      title: (err as Error).message,
    });
    return null;
  }
}

/**
 * Parses the token and returns true if the expiry value is in the past
 */
export function checkJWTExpired(token?: string | null): boolean {
  if (token == null) return true;
  const payload = parseJWT(token);
  if (payload?.exp == null) return true;
  const now = new Date();
  // Date.getTime() returns ms, but exp stores only seconds
  return payload.exp * 1000 < now.getTime();
}

/**
 * Quick and dirty way of making GraphQL requests in places where the Apollo client may not be available.
 *
 * **DO NOT USE!** unless you know exactly what you're doing
 */
export async function fetchGQL<R, V>(
  url: string,
  query: TypedDocumentNode<R, V>,
  variables: V,
): Promise<{ data?: R }> {
  type Body = {
    operationName?: string;
    query: string;
    variables: V;
  };
  const body: Body = {
    variables,
    query: print(query),
  };
  const headers: Record<string, string> = {
    ["Content-Type"]: "application/json",
  };
  const tenant = getTenantHeaderValue();
  if (tenant) headers["x-tenant-id"] = tenant;
  const resp = await fetch(url, {
    body: JSON.stringify(body),
    method: "POST",
    headers,
  });
  if (!resp.ok) {
    const message = await resp.text();
    throw new Error(message);
  }
  const json = await resp.json();
  return json;
}

/**
 * Creates new access and refresh tokens using the refresh token
 */
export async function createNewTokens(
  refreshToken: string,
): Promise<AuthTokens | null> {
  // We need to make a gql request to create new tokens, but don't have access to the Apollo client. Therefore we're using a custom fetch function.
  const { data } = await fetchGQL(
    API_URL,
    gql(/* GraphQL */ `
      mutation CreateNewTokens($refreshToken: String!) {
        createNewTokens(refreshToken: $refreshToken) {
          accessToken
          refreshToken
        }
      }
    `),
    { refreshToken },
  );
  const tokens = data?.createNewTokens;
  if (!tokens) return null;
  return {
    accessToken: tokens.accessToken ?? null,
    refreshToken: tokens.refreshToken ?? null,
  };
}
